import { initializeApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  connectAuthEmulator,
  browserLocalPersistence,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Firebase configuration
// These client values are public and safe to ship.
// Environment variables still take priority for overrides.
const fallbackFirebaseConfig = {
  apiKey: 'AIzaSyAvEhraXeOkpk1rdq_HfeETDcJlls6C_xE',
  authDomain: 'masonic-tools.firebaseapp.com',
  projectId: 'masonic-tools',
  storageBucket: 'masonic-tools.firebasestorage.app',
  messagingSenderId: '286745298063',
  appId: '1:286745298063:web:f8a8a90d6c0636af7f1cfa',
};

// Check if we should use emulators FIRST (opt-in only)
const isDevelopment = import.meta.env.DEV;
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || fallbackFirebaseConfig.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || fallbackFirebaseConfig.authDomain,
  // Ensure the emulator uses the demo project id consistently
  projectId: useEmulators
    ? 'demo-masonic-tools'
    : import.meta.env.VITE_FIREBASE_PROJECT_ID || fallbackFirebaseConfig.projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || fallbackFirebaseConfig.storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || fallbackFirebaseConfig.messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || fallbackFirebaseConfig.appId,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

/**
 * iOS WebKit often loses IndexedDB after backgrounding or memory pressure.
 * Desktop Safari can also hit IndexedDB and long-polling quirks that surface as failed loads in production.
 * Use in-memory Firestore cache only in those cases (and in dev/emulator) so Firestore does not rely on IndexedDB.
 */
function shouldUseFirestorePersistentCache(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  if (isDevelopment || useEmulators) {
    return false;
  }
  const ua = navigator.userAgent;
  const isIosOrIpados =
    /iPhone|iPad|iPod|\biOS\b/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIosOrIpados) {
    return false;
  }
  // Safari (macOS and other non-iOS): UA includes "Safari/" but Chromium-based browsers also include it.
  const looksLikeSafari =
    /Safari\//.test(ua) &&
    !/\bChrome\b|Chromium|CriOS|FxiOS|EdgiOS|\bEdg\b|\bOPR\b/i.test(ua);
  if (looksLikeSafari) {
    return false;
  }
  // In-app browsers sometimes send a desktop UA on a phone; Client Hints still report mobile.
  const uaClaimsMobileOs = /Android|iPhone|iPad|iPod|\biOS\b/i.test(ua);
  const clientHintsMobile =
    (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile ===
    true;
  if (!uaClaimsMobileOs && clientHintsMobile) {
    return false;
  }
  return true;
}

/**
 * Same platforms as Firestore memory cache: avoid Auth persistence layers that use IndexedDB
 * (WebKit / mobile quirks). Use localStorage-only persistence via initializeAuth.
 */
function shouldUseAuthLocalStorageOnly(): boolean {
  return !shouldUseFirestorePersistentCache();
}

function createAuthInstance(): Auth {
  if (typeof window === 'undefined') {
    return getAuth(app);
  }
  if (!shouldUseAuthLocalStorageOnly()) {
    return getAuth(app);
  }
  try {
    return initializeAuth(app, {
      persistence: [browserLocalPersistence],
    });
  } catch {
    // HMR or duplicate module evaluation: auth already initialised
    return getAuth(app);
  }
}

const firestoreSettings = {
  experimentalAutoDetectLongPolling: true,
  localCache: shouldUseFirestorePersistentCache()
    ? persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    : memoryLocalCache(),
};

// Initialize services
export const auth = createAuthInstance();
export const db = initializeFirestore(app, firestoreSettings);
// Always bind Functions to europe-west2 to match deployed region (emulator supported)
export const functions = getFunctions(app, 'europe-west2');
export const storage = getStorage(app);

// Connect to emulators in development mode
if (useEmulators) {
  try {
    // Use "localhost" for all emulator hosts to match firebase.json emulators.*.host and the Vite dev
    // origin (http://localhost:3000). Calling Functions at 127.0.0.1:5001 from localhost:3000 often fails
    // CORS preflight because the browser treats localhost and 127.0.0.1 as different origins.
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8090);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
    
    console.log('🔥 Connected to Firebase Emulators');
    console.log('📊 Emulator UI: http://localhost:4000');
    console.log('📧 Resend configured for LIVE email sending');
  } catch (error) {
    console.error('❌ Could not connect to emulators:', error);
    console.warn('Make sure emulators are running: firebase emulators:start');
  }
} else if (isDevelopment) {
  console.log('🔥 Using live Firebase services (set VITE_USE_EMULATORS=true to use local emulators)');
}

export default app;

